import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Popover,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  TableSortLabel,
  Badge,
  Divider,
  FormHelperText,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar
} from '@mui/material';

import {
  Search,
  FilterList,
  Add,
  Edit,
  Delete,
  Visibility,
  Warning,
  CheckCircle,
  Pending,
  Cancel,
  Download,
  Refresh,
  Category,
  LocalShipping,
  Clear,
  Inventory2,
  Store,
  Assignment,
  Check,
  Close,
  ShoppingCart,
  Verified,
  MoreVert,
  TrendingUp,
  TrendingDown,
  CalendarToday,
  AddCircle,
  ListAlt,
  History,
  EventNote,
  Info,
  NotificationsActive,
  Schedule,
  ErrorOutline,
  HourglassEmpty
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import Sidebar from '../../components/sidebars/Sidebar _inventory';
import TopNav from '../../components/top_bars/TopNav_inventory';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Security: Input sanitization helper
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.trim().replace(/[<>]/g, '');
};

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  maxWidth: '100%',
  '& .MuiTable-root': {
    minWidth: 800,
    width: '100%',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(8px)',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    transition: 'background-color 0.2s ease',
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  borderRadius: '20px',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '28px',
  ...(status === 'In Stock' && {
    backgroundColor: alpha(theme.palette.success.main, 0.12),
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  }),
  ...(status === 'Low Stock' && {
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
    color: theme.palette.warning.main,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
  }),
  ...(status === 'Out of Stock' && {
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    color: theme.palette.error.main,
    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
  }),
  ...(status === 'Approved' && {
    backgroundColor: alpha(theme.palette.success.main, 0.12),
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  }),
  ...(status === 'Pending' && {
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
    color: theme.palette.warning.main,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
  }),
  ...(status === 'Rejected' && {
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    color: theme.palette.error.main,
    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
  }),
  ...(status === 'Backdated (Pending)' && {
    backgroundColor: alpha(theme.palette.info.main, 0.12),
    color: theme.palette.info.main,
    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
  }),
  ...(status === 'Backdated (Approved)' && {
    backgroundColor: alpha(theme.palette.info.main, 0.12),
    color: theme.palette.info.main,
    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
  }),
}));

const GlowingButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

// Custom Alert Dialog for Inventory Alerts
const AlertDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    maxWidth: '600px',
    width: '100%',
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
  }
}));

const AlertSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '&:last-child': {
    marginBottom: 0,
  }
}));

const AlertCard = styled(Box)(({ theme, severity }) => ({
  borderRadius: '16px',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  border: `1px solid ${alpha(
    severity === 'error' ? theme.palette.error.main :
    severity === 'warning' ? theme.palette.warning.main :
    severity === 'info' ? theme.palette.info.main :
    theme.palette.success.main, 0.2
  )}`,
  backgroundColor: alpha(
    severity === 'error' ? theme.palette.error.main :
    severity === 'warning' ? theme.palette.warning.main :
    severity === 'info' ? theme.palette.info.main :
    theme.palette.success.main, 0.04
  ),
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateX(4px)',
    backgroundColor: alpha(
      severity === 'error' ? theme.palette.error.main :
      severity === 'warning' ? theme.palette.warning.main :
      severity === 'info' ? theme.palette.info.main :
      theme.palette.success.main, 0.08
    ),
  }
}));

const InventoryManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [loggedInUser, setLoggedInUser] = useState('');

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // Inventory states (from API)
  const [inventoryItems, setInventoryItems] = useState([]);
  const [totalInventory, setTotalInventory] = useState(0);
  
  // Goods Received (GRN) states (API integrated) - renamed from supplies
  const [goodsReceivedItems, setGoodsReceivedItems] = useState([]);
  const [totalGoodsReceived, setTotalGoodsReceived] = useState(0);
  const [totalApprovedGoodsReceived, setTotalApprovedGoodsReceived] = useState(0);
  const [totalPendingGoodsReceived, setTotalPendingGoodsReceived] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  
  // BACKDATING: States for date received
  const [enableBackdating, setEnableBackdating] = useState(false);
  const [customDateReceived, setCustomDateReceived] = useState('');
  const [backdateReason, setBackdateReason] = useState('');
  const [backdateWarning, setBackdateWarning] = useState('');
  const [daysDifference, setDaysDifference] = useState(0);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGoodsReceived, setSelectedGoodsReceived] = useState(null);
  
  // ALERT DIALOG STATES
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState([]);
  const [hasAlerts, setHasAlerts] = useState(false);
  
  // Form data for goods received (GRN)
  const [formData, setFormData] = useState({
    item_name: '',
    category: '',
    unit_of_measurement: '',
    quantity_received: '',
    total_invoice_value: '',
    checked_by: '',
    expiry_date: ''
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Debug logging utility (only in development)
  const debugLog = (message, data = null) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[DEBUG] ${message}`, data ? data : '');
    }
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Unit options
  const unitOptions = ['pcs', 'kg', 'litres', 'bunches', 'packs', 'boxes', 'reams', 'units'];

  // BACKDATING: Function to validate custom date with enhanced security
  const validateCustomDate = (date) => {
    debugLog('Validating custom date', date);
    
    if (!date) {
      setBackdateWarning('');
      setDaysDifference(0);
      return true;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    
    // Check if date is valid
    if (isNaN(selectedDate.getTime())) {
      setBackdateWarning('Invalid date format');
      setDaysDifference(0);
      return false;
    }
    
    selectedDate.setHours(0, 0, 0, 0);
    
    // Check if date is in the future
    if (selectedDate > today) {
      setBackdateWarning('Cannot set date in the future. Please use today\'s date or a past date.');
      setDaysDifference(0);
      return false;
    }
    
    // Calculate days difference
    const diffTime = today - selectedDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysDifference(diffDays);
    
    if (diffDays > 4) {
      setBackdateWarning(`Cannot backdate more than 4 days. This record would be ${diffDays} days old. Please contact an administrator for older records.`);
      return false;
    } else if (diffDays > 0) {
      setBackdateWarning(`Backdating by ${diffDays} day(s). A reason is required.`);
      return true;
    } else {
      setBackdateWarning('');
      return true;
    }
  };

  // BACKDATING: Handle custom date change
  const handleCustomDateChange = (e) => {
    const date = e.target.value;
    setCustomDateReceived(date);
    validateCustomDate(date);
  };

  // BACKDATING: Reset backdating state when dialog closes
  const resetBackdatingState = () => {
    setEnableBackdating(false);
    setCustomDateReceived('');
    setBackdateReason('');
    setBackdateWarning('');
    setDaysDifference(0);
  };

  // Sorting function
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // ============================================================
  // NEW: Function to check for expiring, low stock, and out of stock products
  // ============================================================
  const checkInventoryAlerts = useCallback(() => {
    debugLog('Checking inventory alerts');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate date 3 weeks from now
    const threeWeeksFromNow = new Date();
    threeWeeksFromNow.setDate(threeWeeksFromNow.getDate() + 21);
    threeWeeksFromNow.setHours(23, 59, 59, 999);
    
    // 1. Find products expiring within 3 weeks
    const expiring = goodsReceivedItems.filter(item => {
      if (!item.expiry_date || item.approval_status !== 1) return false;
      const expiryDate = new Date(item.expiry_date);
      expiryDate.setHours(0, 0, 0, 0);
      return expiryDate >= today && expiryDate <= threeWeeksFromNow;
    }).map(item => ({
      ...item,
      daysUntilExpiry: Math.ceil((new Date(item.expiry_date) - today) / (1000 * 60 * 60 * 24)),
      alertType: 'expiring'
    }));
    
    // 2. Find low stock and out of stock products from inventory
    const lowStock = inventoryItems.filter(item => {
      const stockStatus = getStockStatus(item.quantity_available, item.lowstock_threshold);
      return stockStatus === 'Low Stock';
    }).map(item => ({
      ...item,
      alertType: 'low_stock',
      statusText: 'Low Stock',
      severity: 'warning'
    }));
    
    const outOfStock = inventoryItems.filter(item => {
      const stockStatus = getStockStatus(item.quantity_available, item.lowstock_threshold);
      return stockStatus === 'Out of Stock';
    }).map(item => ({
      ...item,
      alertType: 'out_of_stock',
      statusText: 'Out of Stock',
      severity: 'error'
    }));
    
    setExpiringProducts(expiring);
    setLowStockProducts(lowStock);
    setOutOfStockProducts(outOfStock);
    
    const hasAnyAlerts = expiring.length > 0 || lowStock.length > 0 || outOfStock.length > 0;
    setHasAlerts(hasAnyAlerts);
    
    // Auto-show alert dialog if there are alerts and user hasn't dismissed recently
    const lastAlertDismissed = localStorage.getItem('lastAlertDismissed');
    const shouldShowAlert = hasAnyAlerts && (!lastAlertDismissed || (Date.now() - parseInt(lastAlertDismissed) > 12 * 60 * 60 * 1000));
    
    if (shouldShowAlert && hasAnyAlerts) {
      setAlertDialogOpen(true);
    }
    
    return { expiring, lowStock, outOfStock };
  }, [inventoryItems, goodsReceivedItems]);
  
  // Function to get severity based on days until expiry
  const getExpirySeverity = (daysUntilExpiry) => {
    if (daysUntilExpiry <= 7) return 'error';
    if (daysUntilExpiry <= 14) return 'warning';
    return 'info';
  };
  
  // Function to format days until expiry text
  const getExpiryText = (daysUntilExpiry) => {
    if (daysUntilExpiry === 0) return 'Expires today!';
    if (daysUntilExpiry === 1) return 'Expires tomorrow';
    return `Expires in ${daysUntilExpiry} days`;
  };

  // Fetch inventory items from inventory_main table
  const fetchInventoryItems = useCallback(async () => {
    setLoading(true);
    debugLog('Fetching inventory items', { searchQuery, selectedCategory, selectedStatus, page, rowsPerPage });
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', sanitizeInput(searchQuery));
      if (selectedCategory !== 'all') params.append('category', sanitizeInput(selectedCategory));
      if (selectedStatus !== 'all') params.append('status', sanitizeInput(selectedStatus));
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_inventory_items.php?${params}`);
      if (response.data.success) {
        setInventoryItems(response.data.data);
        setTotalInventory(response.data.total);
        debugLog('Inventory items fetched successfully', { count: response.data.data.length });
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch inventory items',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, selectedStatus, page, rowsPerPage]);

  // Fetch goods received (GRN) from backend
  const fetchGoodsReceived = useCallback(async () => {
    setLoading(true);
    debugLog('Fetching goods received (GRN) items', { searchQuery, selectedCategory, dateRange, page, rowsPerPage });
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', sanitizeInput(searchQuery));
      if (selectedCategory !== 'all') params.append('category', sanitizeInput(selectedCategory));
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_supplies.php?${params}`);
      if (response.data.success) {
        setGoodsReceivedItems(response.data.data);
        setTotalGoodsReceived(response.data.total);
        debugLog('Goods received items fetched successfully', { count: response.data.data.length });
      }
    } catch (error) {
      console.error('Error fetching goods received items:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch goods received items',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, dateRange, page, rowsPerPage]);

  // Fetch goods received statistics (approved and pending counts)
  const fetchGoodsReceivedStats = useCallback(async () => {
    debugLog('Fetching goods received statistics');
    try {
      // Fetch approved goods received count
      const approvedResponse = await axios.get(`${API_BASE_URL}/inventory/get_approved_supplies_count.php`);
      if (approvedResponse.data.success) {
        setTotalApprovedGoodsReceived(approvedResponse.data.count);
        debugLog('Approved count fetched', approvedResponse.data.count);
      }
      
      // Fetch pending goods received count
      const pendingResponse = await axios.get(`${API_BASE_URL}/inventory/get_pending_supplies_count.php`);
      if (pendingResponse.data.success) {
        setTotalPendingGoodsReceived(pendingResponse.data.count);
        debugLog('Pending count fetched', pendingResponse.data.count);
      }
    } catch (error) {
      console.error('Error fetching goods received stats:', error);
    }
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    debugLog('Fetching categories');
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory/get_categories.php`);
      if (response.data.success) {
        setCategories(response.data.categories);
        debugLog('Categories fetched successfully', response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Fetch items list for dropdown
  const fetchItemsList = useCallback(async () => {
    debugLog('Fetching items list for dropdown');
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory/get_item_names.php`);
      if (response.data.success) {
        setItemsList(response.data.items);
        debugLog('Items list fetched successfully', response.data.items.length);
      }
    } catch (error) {
      console.error('Error fetching items list:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      fetchInventoryItems();
      fetchCategories();
    } else if (activeTab === 1) {
      fetchGoodsReceived();
      fetchGoodsReceivedStats();
      fetchCategories();
      fetchItemsList();
    }
  }, [activeTab, fetchInventoryItems, fetchGoodsReceived, fetchGoodsReceivedStats, fetchCategories, fetchItemsList]);

  // NEW: Check alerts whenever inventory items or goods received items change
  useEffect(() => {
    if (inventoryItems.length > 0 || goodsReceivedItems.length > 0) {
      checkInventoryAlerts();
    }
  }, [inventoryItems, goodsReceivedItems, checkInventoryAlerts]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedCategory, selectedStatus, dateRange]);

  // Handle item selection from dropdown with sanitization
  const handleItemSelect = (event, newValue) => {
    debugLog('Item selected from dropdown', newValue);
    if (newValue) {
      setFormData({
        ...formData,
        item_name: sanitizeInput(newValue.item_name),
        category: sanitizeInput(newValue.category),
        unit_of_measurement: sanitizeInput(newValue.unit_of_measurement || '')
      });
    } else {
      setFormData({
        ...formData,
        item_name: '',
        category: '',
        unit_of_measurement: ''
      });
    }
  };

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    const userRole = localStorage.getItem('role');
    
    debugLog('Setting logged in user', { userName, userEmail, userRole });
    
    if (userName) {
      setLoggedInUser(userName);
    } else if (userEmail) {
      setLoggedInUser(userEmail);
    } else {
      setLoggedInUser('Unknown User');
    }
  }, []);

  // Validate form data before submission
  const validateFormData = () => {
    debugLog('Validating form data', formData);
    
    // Check required fields
    if (!formData.item_name || formData.item_name.trim() === '') {
      setSnackbar({
        open: true,
        message: 'Item name is required',
        severity: 'error'
      });
      return false;
    }
    
    if (!formData.category || formData.category.trim() === '') {
      setSnackbar({
        open: true,
        message: 'Category is required',
        severity: 'error'
      });
      return false;
    }
    
    if (!formData.unit_of_measurement || formData.unit_of_measurement.trim() === '') {
      setSnackbar({
        open: true,
        message: 'Unit of measurement is required',
        severity: 'error'
      });
      return false;
    }
    
    // Validate quantity
    const quantityReceived = parseFloat(formData.quantity_received);
    if (isNaN(quantityReceived) || quantityReceived <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid quantity received (greater than 0)',
        severity: 'error'
      });
      return false;
    }
    
    // Validate invoice value if provided
    if (formData.total_invoice_value && formData.total_invoice_value !== '') {
      const invoiceValue = parseFloat(formData.total_invoice_value);
      if (isNaN(invoiceValue) || invoiceValue < 0) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid invoice value',
          severity: 'error'
        });
        return false;
      }
    }
    
    // Validate expiry date if provided
    if (formData.expiry_date) {
      const expiryDate = new Date(formData.expiry_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expiryDate < today) {
        setSnackbar({
          open: true,
          message: 'Expiry date cannot be in the past',
          severity: 'error'
        });
        return false;
      }
    }
    
    return true;
  };

  // Add new goods received (GRN) with backdating support and enhanced security
  const handleAddGoodsReceived = async () => {
    debugLog('Starting handleAddGoodsReceived');
    
    // Validate form data
    if (!validateFormData()) {
      return;
    }

    // Validate backdating if enabled
    if (enableBackdating && customDateReceived) {
      if (!validateCustomDate(customDateReceived)) {
        setSnackbar({
          open: true,
          message: backdateWarning,
          severity: 'error'
        });
        return;
      }
      
      if (daysDifference > 0 && (!backdateReason || backdateReason.trim() === '')) {
        setSnackbar({
          open: true,
          message: 'Please provide a reason for backdating',
          severity: 'error'
        });
        return;
      }
      
      if (daysDifference > 0 && backdateReason.trim().length < 5) {
        setSnackbar({
          open: true,
          message: 'Please provide a more detailed reason for backdating (minimum 5 characters)',
          severity: 'error'
        });
        return;
      }
    }

    setLoading(true);
    try {
      const addedBy = localStorage.getItem('userName') || 
                      localStorage.getItem('email') || 
                      'Unknown User';
      
      // Convert and validate numbers properly
      const quantityReceived = parseFloat(formData.quantity_received);
      
      // Handle total_invoice_value - convert to number or 0
      let totalInvoiceValue = 0;
      if (formData.total_invoice_value && formData.total_invoice_value !== '') {
        totalInvoiceValue = parseFloat(formData.total_invoice_value);
        if (isNaN(totalInvoiceValue)) {
          totalInvoiceValue = 0;
        }
      }
      
      // Build the payload with sanitized data
      const payload = {
        item_name: sanitizeInput(formData.item_name.trim()),
        category: sanitizeInput(formData.category.trim()),
        unit_of_measurement: sanitizeInput(formData.unit_of_measurement.trim()),
        quantity_received: quantityReceived,
        total_invoice_value: totalInvoiceValue,
        added_by_staffid: sanitizeInput(addedBy),
        checked_by: formData.checked_by ? sanitizeInput(formData.checked_by.trim()) : null,
        expiry_date: formData.expiry_date || null
      };
      
      // Add backdating fields if enabled
      if (enableBackdating && customDateReceived) {
        payload.date_received = customDateReceived;
        if (daysDifference > 0) {
          payload.backdate_reason = sanitizeInput(backdateReason.trim());
        }
      }
      
      debugLog('Sending goods received payload', payload);
      
      const response = await axios.post(`${API_BASE_URL}/inventory/add_supply.php`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        let successMessage = response.data.message || 'Goods Received (GRN) added successfully';
        if (response.data.is_backdated) {
          successMessage = `Goods Received (GRN) added with backdated date (${response.data.date_received}). Reason: ${response.data.backdate_reason}. Awaiting approval.`;
        }
        debugLog('Goods received added successfully', response.data);
        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
        handleCloseAddDialog();
        fetchGoodsReceived();
        fetchGoodsReceivedStats();
        fetchItemsList();
      } else {
        throw new Error(response.data.message || 'Failed to add Goods Received (GRN)');
      }
    } catch (error) {
      console.error('Error adding goods received:', error);
      
      // More detailed error handling
      let errorMessage = 'Failed to add Goods Received (GRN)';
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit goods received (GRN)
  const handleEditGoodsReceived = async () => {
    debugLog('Starting handleEditGoodsReceived', { selectedGoodsReceived, formData });
    
    if (!validateFormData()) {
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        id: selectedGoodsReceived.id,
        item_name: sanitizeInput(formData.item_name),
        category: sanitizeInput(formData.category),
        unit_of_measurement: sanitizeInput(formData.unit_of_measurement),
        quantity_received: parseFloat(formData.quantity_received),
        total_invoice_value: parseFloat(formData.total_invoice_value) || 0,
        checked_by: formData.checked_by ? sanitizeInput(formData.checked_by) : null,
        expiry_date: formData.expiry_date || null
      };

      debugLog('Edit goods received payload', payload);
      
      const response = await axios({
        method: 'put',
        url: `${API_BASE_URL}/inventory/update_supply.php`,
        data: payload,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        debugLog('Goods received updated successfully', response.data);
        setSnackbar({
          open: true,
          message: 'Goods Received (GRN) updated successfully',
          severity: 'success'
        });
        handleCloseEditDialog();
        fetchGoodsReceived();
        fetchGoodsReceivedStats();
        fetchItemsList();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error updating goods received:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to update goods received',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete goods received (GRN)
  const handleDeleteGoodsReceived = async () => {
    debugLog('Starting handleDeleteGoodsReceived', { selectedGoodsReceived });
    setLoading(true);
    try {
      const response = await axios({
        method: 'delete',
        url: `${API_BASE_URL}/inventory/delete_supply.php`,
        data: { id: selectedGoodsReceived.id },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        debugLog('Goods received deleted successfully', response.data);
        setSnackbar({
          open: true,
          message: 'Goods Received (GRN) deleted successfully',
          severity: 'success'
        });
        handleCloseDeleteDialog();
        fetchGoodsReceived();
        fetchGoodsReceivedStats();
        fetchItemsList();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error deleting goods received:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to delete goods received',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel with sanitized data
  const handleExportToExcel = () => {
    debugLog('Exporting data to Excel', { activeTab });
    let exportData = [];
    let fileName = '';
    
    if (activeTab === 0) {
      exportData = inventoryItems.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Quantity Received': item.quantity_received,
        'Quantity Issued': item.quantity_issued,
        'Quantity Available': item.quantity_available,
        'Quantity Damaged': item.quantity_damaged,
        'Storage Location': item.storage_location || 'N/A',
        'Date Received': item.date_received,
        'Added By': item.added_by_staffid,
        'Notes': item.notes || 'N/A'
      }));
      fileName = 'inventory_items';
    } else if (activeTab === 1) {
      exportData = goodsReceivedItems.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category,
        'Unit': item.unit_of_measurement,
        'Quantity Received': item.quantity_received,
        'Total Invoice Value (₦)': item.total_invoice_value,
        'Date Received': item.date_received,
        'Approval Status': item.display_status || (item.approval_status === 1 ? 'Approved' : 'Pending'),
        'Added By': item.added_by_staffid,
        'Expiry Date': item.expiry_date || 'N/A',
        'Is Backdated': item.is_backdated ? 'Yes' : 'No',
        'Backdate Reason': item.backdate_reason || 'N/A',
        'Backdated By': item.backdated_by || 'N/A'
      }));
      fileName = 'goods_received_grn';
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    debugLog('Export completed successfully');
    setSnackbar({
      open: true,
      message: 'Export successful!',
      severity: 'success'
    });
  };

  const handleOpenAddDialog = () => {
    debugLog('Opening add goods received dialog');
    setFormData({
      item_name: '',
      category: '',
      unit_of_measurement: '',
      quantity_received: '',
      total_invoice_value: '',
      checked_by: '',
      expiry_date: ''
    });
    resetBackdatingState();
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = (item) => {
    debugLog('Opening edit goods received dialog', { item });
    if (item.approval_status === 1) {
      setSnackbar({
        open: true,
        message: 'Cannot edit approved goods received. Please request approval for changes.',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedGoodsReceived(item);
    setFormData({
      item_name: item.item_name,
      category: item.category,
      unit_of_measurement: item.unit_of_measurement,
      quantity_received: item.quantity_received,
      total_invoice_value: item.total_invoice_value,
      checked_by: item.checked_by || '',
      expiry_date: item.expiry_date || ''
    });
    setEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (item) => {
    debugLog('Opening delete goods received dialog', { item });
    if (item.approval_status === 1) {
      setSnackbar({
        open: true,
        message: 'Cannot delete approved goods received. Please request deletion approval.',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedGoodsReceived(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    debugLog('Closing add goods received dialog');
    setAddDialogOpen(false);
    resetBackdatingState();
  };
  
  const handleCloseEditDialog = () => {
    debugLog('Closing edit goods received dialog');
    setEditDialogOpen(false);
  };
  
  const handleCloseDeleteDialog = () => {
    debugLog('Closing delete goods received dialog');
    setDeleteDialogOpen(false);
  };
  
  // NEW: Alert dialog handlers
  const handleOpenAlertDialog = () => {
    debugLog('Opening alert dialog');
    setAlertDialogOpen(true);
  };
  
  const handleCloseAlertDialog = () => {
    debugLog('Closing alert dialog');
    setAlertDialogOpen(false);
    // Store dismissal timestamp to prevent showing for 12 hours
    localStorage.setItem('lastAlertDismissed', Date.now().toString());
  };

  const handleTabChange = (event, newValue) => {
    debugLog('Tab changed', { newValue });
    setActiveTab(newValue);
    setPage(0);
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setDateRange({ start: '', end: '' });
    setOrderBy('');
    setOrder('asc');
  };

  const handleFilterClick = (event) => {
    debugLog('Filter button clicked');
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    debugLog('Filter popover closed');
    setFilterAnchorEl(null);
  };

  const clearFilters = () => {
    debugLog('Clearing all filters');
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setDateRange({ start: '', end: '' });
    setPage(0);
    handleFilterClose();
    if (activeTab === 0) {
      fetchInventoryItems();
    } else if (activeTab === 1) {
      fetchGoodsReceived();
      fetchGoodsReceivedStats();
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'In Stock': return <CheckCircle fontSize="small" />;
      case 'Low Stock': return <Warning fontSize="small" />;
      case 'Out of Stock': return <Cancel fontSize="small" />;
      case 'Approved': return <CheckCircle fontSize="small" />;
      case 'Pending': return <Pending fontSize="small" />;
      case 'Rejected': return <Cancel fontSize="small" />;
      case 'Backdated (Pending)': return <History fontSize="small" />;
      case 'Backdated (Approved)': return <EventNote fontSize="small" />;
      default: return null;
    }
  };

  const getStockStatus = (quantityAvailable, lowstockThreshold = 5) => {
    if (quantityAvailable <= 0) return 'Out of Stock';
    if (quantityAvailable <= lowstockThreshold) return 'Low Stock';
    return 'In Stock';
  };

  // Sort data
  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_received' || orderBy === 'quantity_available' || 
          orderBy === 'quantity_issued' || orderBy === 'total_invoice_value') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'date_received' || orderBy === 'created_at' || orderBy === 'lastUpdated' || orderBy === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Get unique categories from inventory items
  const getInventoryCategories = () => {
    const uniqueCategories = [...new Set(inventoryItems.map(item => item.category).filter(Boolean))];
    return ['all', ...uniqueCategories];
  };

  // Render Inventory Table from inventory_main
  const renderInventoryTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Received
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_issued'}
                direction={orderBy === 'quantity_issued' ? order : 'asc'}
                onClick={() => handleSort('quantity_issued')}
              >
                Issued
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_available'}
                direction={orderBy === 'quantity_available' ? order : 'asc'}
                onClick={() => handleSort('quantity_available')}
              >
                Available
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Status</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>Storage Location</TableCell>
            <TableCell align="center" sx={{ minWidth: 100 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : inventoryItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No inventory items found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortData(inventoryItems).map((item) => {
              const stockStatus = getStockStatus(item.quantity_available, item.lowstock_threshold);
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Inventory2 sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.category || 'Uncategorized'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {Number(item.quantity_received).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {Number(item.quantity_issued).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={stockStatus === 'Low Stock' ? 700 : 500}>
                      {Number(item.quantity_available).toLocaleString()}
                    </Typography>
                    {stockStatus === 'Low Stock' && item.lowstock_threshold && (
                      <LinearProgress 
                        variant="determinate" 
                        value={(item.quantity_available / (item.lowstock_threshold * 2)) * 100} 
                        sx={{ mt: 0.5, height: 3, borderRadius: 2 }}
                        color="warning"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip 
                      icon={getStatusIcon(stockStatus)}
                      label={stockStatus} 
                      status={stockStatus}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{item.date_received}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.storage_location || 'Not specified'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => {
                        setSnackbar({
                          open: true,
                          message: `View details for ${item.item_name} - Coming soon`,
                          severity: 'info'
                        });
                      }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 25, 50, 100]}
        component="div"
        count={totalInventory}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Render Goods Received (GRN) Table with backdate information
  const renderGoodsReceivedTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 200 }}>
              <TableSortLabel
                active={orderBy === 'item_name'}
                direction={orderBy === 'item_name' ? order : 'asc'}
                onClick={() => handleSort('item_name')}
              >
                Item Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell align="right" sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'quantity_received'}
                direction={orderBy === 'quantity_received' ? order : 'asc'}
                onClick={() => handleSort('quantity_received')}
              >
                Received Qty
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 150 }}>
              <TableSortLabel
                active={orderBy === 'total_invoice_value'}
                direction={orderBy === 'total_invoice_value' ? order : 'asc'}
                onClick={() => handleSort('total_invoice_value')}
              >
                Total Invoice (₦)
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <TableSortLabel
                active={orderBy === 'date_received'}
                direction={orderBy === 'date_received' ? order : 'asc'}
                onClick={() => handleSort('date_received')}
              >
                Date Received
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ minWidth: 140 }}>Approval Status</TableCell>
            <TableCell sx={{ minWidth: 150 }}>Added By</TableCell>
            <TableCell sx={{ minWidth: 120 }}>Expiry Date</TableCell>
            <TableCell sx={{ minWidth: 100 }}>Backdated</TableCell>
            <TableCell align="center" sx={{ minWidth: 120 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : goodsReceivedItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">No goods received records found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortData(goodsReceivedItems).map((item) => {
              const displayStatus = item.display_status || (item.approval_status === 1 ? 'Approved' : 'Pending');
              const isBackdated = item.is_backdated === true;
              const showBackdateWarning = isBackdated && item.approval_status === 0 && item.days_since_backdate >= 4;
              
              return (
                <TableRow 
                  key={item.id} 
                  hover
                  sx={{
                    ...(showBackdateWarning && {
                      backgroundColor: alpha(theme.palette.error.main, 0.05),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      }
                    })
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                        <Store sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.category} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{item.unit_of_measurement}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {Number(item.quantity_received).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      ₦{Number(item.total_invoice_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{item.date_received}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip 
                      title={isBackdated && item.approval_status === 0 ? 
                        `Backdated: ${item.backdate_reason || 'No reason provided'}` : 
                        ''}
                    >
                      <StatusChip 
                        icon={getStatusIcon(displayStatus)}
                        label={displayStatus} 
                        status={displayStatus}
                        size="small"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`Added by: ${item.added_by_staffid || 'Unknown'}`}>
                      <Chip 
                        label={item.added_by_staffid || 'Unknown'}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: '150px' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {item.expiry_date && (
                      <Typography 
                        variant="caption" 
                        color={new Date(item.expiry_date) < new Date() ? 'error.main' : 'text.secondary'}
                      >
                        {item.expiry_date}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isBackdated ? (
                      <Tooltip title={`Backdated reason: ${item.backdate_reason || 'N/A'}\nBackdated by: ${item.backdated_by || 'N/A'}`}>
                        <Chip 
                          icon={<History fontSize="small" />}
                          label="Yes"
                          size="small"
                          color={item.approval_status === 0 && item.days_since_backdate >= 4 ? "error" : "info"}
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="No" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={item.approval_status === 1 ? "Cannot edit approved goods received" : "Edit"}>
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenEditDialog(item)}
                          disabled={item.approval_status === 1}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={item.approval_status === 1 ? "Cannot delete approved goods received" : "Delete"}>
                      <span>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => handleOpenDeleteDialog(item)}
                          disabled={item.approval_status === 1}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 25, 50, 100]}
        component="div"
        count={totalGoodsReceived}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  const renderCurrentTable = () => {
    if (activeTab === 0) return renderInventoryTable();
    if (activeTab === 1) return renderGoodsReceivedTable();
    return null;
  };

  const getAddButtonText = () => {
    if (activeTab === 0) return 'Add Inventory Item';
    if (activeTab === 1) return 'Add Goods Received (GRN)';
    if (activeTab === 2) return 'Add Requisition';
    return 'Add';
  };

  const handleAddClick = () => {
    if (activeTab === 1) {
      handleOpenAddDialog();
    } else {
      setSnackbar({
        open: true,
        message: `${getAddButtonText()} feature coming soon`,
        severity: 'info'
      });
    }
  };

  // Statistics
  const statistics = useMemo(() => {
    if (activeTab === 0) {
      const lowStock = inventoryItems.filter(item => {
        const stockStatus = getStockStatus(item.quantity_available, item.lowstock_threshold);
        return stockStatus === 'Low Stock';
      }).length;
      const outOfStock = inventoryItems.filter(item => {
        const stockStatus = getStockStatus(item.quantity_available, item.lowstock_threshold);
        return stockStatus === 'Out of Stock';
      }).length;
      return { totalItems: totalInventory, lowStock, outOfStock };
    } else if (activeTab === 1) {
      // Count backdated pending goods received
      const backdatedPending = goodsReceivedItems.filter(s => s.is_backdated === true && s.approval_status === 0).length;
      return { 
        totalItems: totalGoodsReceived, 
        approvedGoodsReceived: totalApprovedGoodsReceived, 
        pendingGoodsReceived: totalPendingGoodsReceived,
        backdatedPending: backdatedPending
      };
    }
    return { totalItems: 0 };
  }, [activeTab, inventoryItems, totalInventory, totalGoodsReceived, totalApprovedGoodsReceived, totalPendingGoodsReceived, goodsReceivedItems]);

  // NEW: Render Alert Dialog
  const renderAlertDialog = () => (
    <AlertDialog
      open={alertDialogOpen}
      onClose={handleCloseAlertDialog}
      scroll="paper"
    >
      <DialogTitle sx={{ 
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}>
        <Avatar sx={{ 
          bgcolor: alpha(theme.palette.warning.main, 0.15), 
          color: theme.palette.warning.main,
          width: 40,
          height: 40
        }}>
          <NotificationsActive />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Inventory Alerts
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Items requiring your attention
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        {/* Expiring Products Section */}
        {expiringProducts.length > 0 && (
          <AlertSection>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Schedule sx={{ color: theme.palette.warning.main }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Expiring Within 3 Weeks ({expiringProducts.length})
              </Typography>
            </Box>
            {expiringProducts.map((product, index) => {
              const severity = getExpirySeverity(product.daysUntilExpiry);
              return (
                <AlertCard key={`expiring-${product.id}-${index}`} severity={severity}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="body1" fontWeight={600}>
                          {product.item_name}
                        </Typography>
                        <Chip 
                          label={getExpiryText(product.daysUntilExpiry)}
                          size="small"
                          color={severity}
                          icon={severity === 'error' ? <ErrorOutline /> : <HourglassEmpty />}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Category: {product.category} | Unit: {product.unit_of_measurement}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Expiry Date: {product.expiry_date} | Quantity: {Number(product.quantity_received).toLocaleString()} {product.unit_of_measurement}
                      </Typography>
                    </Box>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => {
                        setActiveTab(1);
                        setAlertDialogOpen(false);
                      }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AlertCard>
              );
            })}
          </AlertSection>
        )}
        
        {/* Low Stock Products Section */}
        {lowStockProducts.length > 0 && (
          <AlertSection>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Warning sx={{ color: theme.palette.warning.main }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Low Stock Items ({lowStockProducts.length})
              </Typography>
            </Box>
            {lowStockProducts.map((product, index) => (
              <AlertCard key={`lowstock-${product.id}-${index}`} severity="warning">
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body1" fontWeight={600}>
                        {product.item_name}
                      </Typography>
                      <Chip 
                        label={`${Number(product.quantity_available).toLocaleString()} ${product.unit_of_measurement} left`}
                        size="small"
                        color="warning"
                        icon={<Warning />}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Category: {product.category} | Threshold: {product.lowstock_threshold} {product.unit_of_measurement}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Storage: {product.storage_location || 'Not specified'} | Last Received: {product.date_received}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={(product.quantity_available / (product.lowstock_threshold * 2)) * 100} 
                      sx={{ mt: 1, height: 4, borderRadius: 2 }}
                      color="warning"
                    />
                  </Box>
                  <Tooltip title="View in Inventory">
                    <IconButton size="small" onClick={() => {
                      setActiveTab(0);
                      setAlertDialogOpen(false);
                    }}>
                      <Inventory2 fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AlertCard>
            ))}
          </AlertSection>
        )}
        
        {/* Out of Stock Products Section */}
        {outOfStockProducts.length > 0 && (
          <AlertSection>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Cancel sx={{ color: theme.palette.error.main }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Out of Stock Items ({outOfStockProducts.length})
              </Typography>
            </Box>
            {outOfStockProducts.map((product, index) => (
              <AlertCard key={`outofstock-${product.id}-${index}`} severity="error">
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body1" fontWeight={600}>
                        {product.item_name}
                      </Typography>
                      <Chip 
                        label="Out of Stock"
                        size="small"
                        color="error"
                        icon={<Cancel />}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Category: {product.category} | Unit: {product.unit_of_measurement}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Storage: {product.storage_location || 'Not specified'} | Last Received: {product.date_received}
                    </Typography>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="primary"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setActiveTab(1);
                        handleOpenAddDialog();
                        setFormData({
                          ...formData,
                          item_name: product.item_name,
                          category: product.category,
                          unit_of_measurement: product.unit_of_measurement
                        });
                        setAlertDialogOpen(false);
                      }}
                    >
                      <Add sx={{ fontSize: 16, mr: 0.5 }} />
                      Reorder Now
                    </Button>
                  </Box>
                  <Tooltip title="View in Inventory">
                    <IconButton size="small" onClick={() => {
                      setActiveTab(0);
                      setAlertDialogOpen(false);
                    }}>
                      <Inventory2 fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AlertCard>
            ))}
          </AlertSection>
        )}
        
        {/* No Alerts Message */}
        {expiringProducts.length === 0 && lowStockProducts.length === 0 && outOfStockProducts.length === 0 && (
          <Box textAlign="center" py={4}>
            <Avatar sx={{ 
              mx: 'auto', 
              mb: 2, 
              bgcolor: alpha(theme.palette.success.main, 0.1),
              width: 56,
              height: 56
            }}>
              <CheckCircle sx={{ fontSize: 32, color: theme.palette.success.main }} />
            </Avatar>
            <Typography variant="h6" color="text.primary" gutterBottom>
              All Good!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No expiring, low stock, or out of stock items at this time.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <Button onClick={handleCloseAlertDialog} variant="outlined">
          Dismiss
        </Button>
        {(expiringProducts.length > 0 || lowStockProducts.length > 0 || outOfStockProducts.length > 0) && (
          <Button 
            variant="contained" 
            onClick={() => {
              if (expiringProducts.length > 0) {
                setActiveTab(1); // Go to Goods Received (GRN) tab for expiring products
              } else if (lowStockProducts.length > 0 || outOfStockProducts.length > 0) {
                setActiveTab(0); // Go to Inventory tab for stock issues
              }
              setAlertDialogOpen(false);
            }}
          >
            View {expiringProducts.length > 0 ? 'Expiring Products' : 'Inventory Items'}
          </Button>
        )}
      </DialogActions>
    </AlertDialog>
  );

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh', overflowX: 'hidden' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        transition: 'margin 0.3s ease-in-out',
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%',
        overflowX: 'hidden'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 3, width: '100%', overflowX: 'hidden' }}>
          <Paper elevation={0} sx={{ borderRadius: '20px', mb: 3, overflow: 'hidden', width: '100%' }}>
            {/* Alert Banner - Shows when there are active alerts */}
            {hasAlerts && (
              <Alert 
                severity="warning" 
                icon={<NotificationsActive />}
                sx={{ 
                  borderRadius: 0, 
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleOpenAlertDialog}>
                    View Details
                  </Button>
                }
              >
                <Typography variant="body2">
                  {expiringProducts.length > 0 && `${expiringProducts.length} product(s) expiring within 3 weeks. `}
                  {lowStockProducts.length > 0 && `${lowStockProducts.length} item(s) low on stock. `}
                  {outOfStockProducts.length > 0 && `${outOfStockProducts.length} item(s) out of stock. `}
                  Click "View Details" for more information.
                </Typography>
              </Alert>
            )}
            
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              sx={{
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  py: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }
                },
                '& .Mui-selected': {
                  color: theme.palette.primary.main,
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                }
              }}
            >
              <Tab icon={<Inventory2 />} iconPosition="start" label={`Inventory (${statistics.totalItems})`} />
              <Tab icon={<Store />} iconPosition="start" label={`Goods Received (GRN) (${statistics.totalItems})`} />
            </Tabs>

            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ p: 3, backgroundColor: alpha(theme.palette.primary.main, 0.01) }}>
              <Grid item xs={12} sm={activeTab === 2 ? 6 : activeTab === 1 ? 4 : 4}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          Total {activeTab === 0 ? 'Items' : activeTab === 1 ? 'Goods Received (GRN)' : 'Requisitions'}
                        </Typography>
                        <Typography variant="h4" fontWeight={700} mt={1}>
                          {statistics.totalItems}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                        {activeTab === 0 ? <Inventory2 /> : activeTab === 1 ? <Store /> : <Assignment />}
                      </Avatar>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              {activeTab === 1 && (
                <>
                  <Grid item xs={12} sm={4}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Approved Goods Received
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="success.main">
                              {statistics.approvedGoodsReceived}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                            <Verified />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Pending Goods Received
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="warning.main">
                              {statistics.pendingGoodsReceived}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                            <Pending />
                          </Avatar>
                        </Box>
                        {statistics.backdatedPending > 0 && (
                          <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
                            <History sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {statistics.backdatedPending} backdated pending
                          </Typography>
                        )}
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </>
              )}
              
              {activeTab === 0 && (
                <>
                  <Grid item xs={12} sm={4}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Low Stock Alert
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="warning.main">
                              {statistics.lowStock}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                            <Warning />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <StyledCard>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              Out of Stock
                            </Typography>
                            <Typography variant="h4" fontWeight={700} mt={1} color="error.main">
                              {statistics.outOfStock}
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                            <Cancel />
                          </Avatar>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </>
              )}
            </Grid>

            {/* Search and Filters Bar */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by name, SKU, category, or requester..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <GlowingButton
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={handleFilterClick}
                    >
                      Filters
                    </GlowingButton>
                    
                    {/* Alert Button */}
                    {hasAlerts && (
                      <GlowingButton
                        variant="outlined"
                        color="warning"
                        startIcon={
                          <Badge badgeContent={expiringProducts.length + lowStockProducts.length + outOfStockProducts.length} color="error">
                            <NotificationsActive />
                          </Badge>
                        }
                        onClick={handleOpenAlertDialog}
                      >
                        Alerts
                      </GlowingButton>
                    )}
                    
                    {/* Only show Add Goods Received button on Goods Received (GRN) tab */}
                    {activeTab === 1 && (
                      <GlowingButton
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenAddDialog}
                      >
                        Add Goods Received (GRN)
                      </GlowingButton>
                    )}
                    
                    <GlowingButton
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleExportToExcel}
                    >
                      Export
                    </GlowingButton>
                    
                    <IconButton onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setSelectedStatus('all');
                      setDateRange({ start: '', end: '' });
                      if (activeTab === 1) fetchGoodsReceived();
                    }}>
                      <Refresh />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
            
            {/* Active Filters Display */}
            {(selectedCategory !== 'all' || selectedStatus !== 'all' || dateRange.start) && (
              <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Active Filters:</Typography>
                {selectedCategory !== 'all' && (
                  <Chip label={`Category: ${selectedCategory}`} onDelete={() => setSelectedCategory('all')} size="small" />
                )}
                {selectedStatus !== 'all' && (
                  <Chip label={`Status: ${selectedStatus}`} onDelete={() => setSelectedStatus('all')} size="small" />
                )}
                {dateRange.start && (
                  <Chip label={`From: ${dateRange.start}`} onDelete={() => setDateRange({ ...dateRange, start: '' })} size="small" />
                )}
                {dateRange.end && (
                  <Chip label={`To: ${dateRange.end}`} onDelete={() => setDateRange({ ...dateRange, end: '' })} size="small" />
                )}
                <Button size="small" onClick={clearFilters} sx={{ ml: 1 }}>Clear All</Button>
              </Box>
            )}

            {/* Table */}
            {renderCurrentTable()}
          </Paper>
        </Box>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, width: 320, borderRadius: '16px' } }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Filters</Typography>
        
        {(activeTab === 0 || activeTab === 1) && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Category">
              <MenuItem value="all">All Categories</MenuItem>
              {activeTab === 0 
                ? getInventoryCategories().filter(c => c !== 'all').map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))
                : categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))
              }
            </Select>
          </FormControl>
        )}
        
        {(activeTab === 0) && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="In Stock">In Stock</MenuItem>
              <MenuItem value="Low Stock">Low Stock</MenuItem>
              <MenuItem value="Out of Stock">Out of Stock</MenuItem>
            </Select>
          </FormControl>
        )}
        
        {(activeTab === 1 || activeTab === 2) && (
          <>
            <TextField
              fullWidth size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }}
              value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }}
              value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              sx={{ mb: 2 }}
            />
          </>
        )}
        
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={clearFilters} size="small">Clear All</Button>
          <Button variant="contained" onClick={handleFilterClose} size="small">Apply</Button>
        </Stack>
      </Popover>

      {/* Add Dialog for Goods Received (GRN) with Backdating Feature */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>Add Goods Received (GRN)</Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={itemsList}
                getOptionLabel={(option) => option.item_name}
                onChange={handleItemSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Select Item Name"
                    placeholder="Search or select an item..."
                    helperText="Select an item to auto-fill category and unit"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>{option.item_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Category: {option.category} | Unit: {option.unit_of_measurement}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Category"
                value={formData.category}
                disabled
                helperText="Auto-filled from selected item"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Unit of Measurement"
                value={formData.unit_of_measurement}
                disabled
                helperText="Auto-filled from selected item"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Quantity Received"
                value={formData.quantity_received}
                onChange={(e) => setFormData({ ...formData, quantity_received: e.target.value })}
                InputProps={{ inputProps: { min: 0, step: 0.001 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Total Invoice Value (₦)"
                value={formData.total_invoice_value}
                onChange={(e) => setFormData({ ...formData, total_invoice_value: e.target.value })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date (Optional)"
                InputLabelProps={{ shrink: true }}
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
              />
            </Grid>
            
            {/* BACKDATING SECTION */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Chip 
                  icon={<History />} 
                  label="Date Received Settings" 
                  size="small" 
                  variant="outlined"
                />
              </Divider>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={enableBackdating}
                    onChange={(e) => {
                      setEnableBackdating(e.target.checked);
                      if (!e.target.checked) {
                        setCustomDateReceived('');
                        setBackdateReason('');
                        setBackdateWarning('');
                      } else {
                        // Set default to today's date
                        const today = new Date().toISOString().split('T')[0];
                        setCustomDateReceived(today);
                      }
                    }}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <EventNote fontSize="small" />
                    <Typography variant="body2">Set custom date received (Backdating)</Typography>
                  </Box>
                }
              />
              
              {enableBackdating && (
                <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    <Info sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                    You can backdate up to 4 days. A reason is required for backdating.
                  </Typography>
                  
                  <TextField
                    fullWidth
                    type="date"
                    label="Date Received"
                    value={customDateReceived}
                    onChange={handleCustomDateChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mt: 1, mb: 2 }}
                    error={!!backdateWarning && daysDifference > 0}
                    helperText={backdateWarning}
                  />
                  
                  {daysDifference > 0 && (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Reason for Backdating"
                      placeholder="Please provide a detailed reason why this goods received is being backdated..."
                      value={backdateReason}
                      onChange={(e) => setBackdateReason(e.target.value)}
                      required
                      error={backdateReason.length > 0 && backdateReason.length < 5}
                      helperText={
                        backdateReason.length > 0 && backdateReason.length < 5 
                          ? "Please provide at least 5 characters" 
                          : "This reason will be recorded for audit purposes"
                      }
                    />
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddGoodsReceived} 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Goods Received (GRN)'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog for Goods Received (GRN) */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Goods Received (GRN)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Item Name"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Unit of Measurement</InputLabel>
                <Select
                  value={formData.unit_of_measurement}
                  onChange={(e) => setFormData({ ...formData, unit_of_measurement: e.target.value })}
                  label="Unit of Measurement"
                >
                  {unitOptions.map(unit => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Quantity Received"
                value={formData.quantity_received}
                onChange={(e) => setFormData({ ...formData, quantity_received: e.target.value })}
                InputProps={{ inputProps: { min: 0, step: 0.001 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Total Invoice Value (₦)"
                value={formData.total_invoice_value}
                onChange={(e) => setFormData({ ...formData, total_invoice_value: e.target.value })}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Checked By"
                value={formData.checked_by}
                onChange={(e) => setFormData({ ...formData, checked_by: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
              />
            </Grid>
            
            {selectedGoodsReceived?.is_backdated === 1 && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<History />}>
                  <Typography variant="body2" fontWeight={600}>Backdating Information</Typography>
                  <Typography variant="caption" display="block">
                    This record was backdated to: {selectedGoodsReceived.date_received}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Reason: {selectedGoodsReceived.backdate_reason || 'No reason provided'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Backdated by: {selectedGoodsReceived.backdated_by || 'Unknown'}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleEditGoodsReceived} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedGoodsReceived?.item_name}"? This action cannot be undone.
          </Typography>
          {selectedGoodsReceived?.approval_status === 1 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This goods received record is approved and cannot be deleted. Please request deletion approval.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteGoodsReceived}
            disabled={loading || selectedGoodsReceived?.approval_status === 1}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT DIALOG */}
      {renderAlertDialog()}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryManagement;